import React from 'react';
import './StupidSystemDesigns.css';

const hashSetDesignCode = String.raw`
#include <bits/stdc++.h>
using namespace std;

class HashSet {
    int numElements;
    int capacity;
    vector<vector<string>> buckets;
    double loadFactor;

    int getIndex(const string& key) {
        long long hashIndex = 0;
        for (char c : key) {
            hashIndex = (hashIndex * 31 + (unsigned char)c) % capacity;
        }
        return hashIndex;
    }

    bool exceedsLoadFactor() {
        double presentLoad = (double)numElements / (double)capacity;
        return presentLoad > loadFactor;
    }

    void rehash() {
        cout << "\n  [REHASH] Load factor threshold exceeded (" 
             << (double)numElements / capacity << " > " << loadFactor << ")" << endl;
        cout << "  [REHASH] Resizing capacity: " << capacity << " -> " << capacity * 2 << endl;

        capacity *= 2;
        vector<vector<string>> newBuckets(capacity);
        for (auto &bucket : buckets) {
            for (auto &key : bucket) {
                int newHashIndex = getIndex(key);
                newBuckets[newHashIndex].push_back(key);
            }
        }
        buckets = std::move(newBuckets);
    }

public:
    HashSet(int cap = 4, double loadFac = 0.75) {
        numElements = 0;
        capacity = cap;
        buckets.assign(capacity, {});
        loadFactor = loadFac;
    }

    void insert(string key) {
        int hashIndex = getIndex(key);
        for (auto &ele : buckets[hashIndex]) {
            if (ele == key) return;
        }
        buckets[hashIndex].push_back(key);
        numElements++;
        if (exceedsLoadFactor()) rehash();
    }

    void remove(string key) {
        int hashIndex = getIndex(key);
        for (int i = 0; i < buckets[hashIndex].size(); i++) {
            if (buckets[hashIndex][i] == key) {
                swap(buckets[hashIndex][i], buckets[hashIndex].back());
                buckets[hashIndex].pop_back();
                numElements--;
                return;
            }
        }
    }

    bool contains(string key) {
        int hashIndex = getIndex(key);
        for (auto &ele : buckets[hashIndex]) {
            if (ele == key) return true;
        }
        return false;
    }

    // --- Inspection Helpers for Driver Code ---
    int size() const { return numElements; }
    int getCapacity() const { return capacity; }
    double getCurrentLoadFactor() const { return (double)numElements / capacity; }

    void printState() const {
        cout << "--------------------------------------------------" << endl;
        cout << "HashSet State: Size = " << numElements 
             << " | Capacity = " << capacity 
             << " | Load Factor = " << fixed << setprecision(2) << getCurrentLoadFactor() << endl;
        for (int i = 0; i < capacity; i++) {
            cout << "  Bucket [" << i << "]: ";
            if (buckets[i].empty()) {
                cout << "[empty]";
            } else {
                for (const auto& item : buckets[i]) {
                    cout << "\"" << item << "\" ";
                }
            }
            cout << endl;
        }
        cout << "--------------------------------------------------" << endl;
    }
};

int main() {
    HashSet set(4, 0.75);

    cout << "=== 1. INITIAL STATE ===" << endl;
    set.printState();

    cout << "\n=== 2. INSERTING INITIAL ELEMENTS ===" << endl;
    vector<string> items = {"apple", "banana", "cherry"};
    for (const string& item : items) {
        cout << "Inserting: \"" << item << "\"" << endl;
        set.insert(item);
    }
    set.printState();

    cout << "\n=== 3. TESTING DUPLICATE PREVENTION ===" << endl;
    cout << "Inserting duplicate: \"apple\"" << endl;
    set.insert("apple");
    cout << "Current Size: " << set.size() << " (Expected: 3)" << endl;

    cout << "\n=== 4. TRIGGERING AUTO-REHASH ===" << endl;
    cout << "Inserting 4th element (\"date\") to push Load Factor (4/4 = 1.0 > 0.75)..." << endl;
    set.insert("date"); 
    set.printState();

    cout << "\n=== 5. TESTING SEARCH ('contains') ===" << endl;
    cout << boolalpha;
    cout << "Contains \"banana\"? " << set.contains("banana") << " (Expected: true)" << endl;
    cout << "Contains \"elderberry\"? " << set.contains("elderberry") << " (Expected: false)" << endl;

    cout << "\n=== 6. TESTING DELETION ('remove') ===" << endl;
    cout << "Removing: \"banana\"" << endl;
    set.remove("banana");
    cout << "Contains \"banana\" after removal? " << set.contains("banana") << " (Expected: false)" << endl;

    cout << "\nAttempting to remove non-existent key \"dragonfruit\"..." << endl;
    set.remove("dragonfruit"); 

    set.printState();

    return 0;
}
`;

function HashSetDesign({ theme, onBackClick }) {
    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>HashSet Design</h1>
                            <p className="system-designs-subtitle">
                                A constant-time collection built by trading a little extra memory for fast insertion and retrieval.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Requirements</h2>
                            <ul>
                                <li>Insert an element in constant time.</li>
                                <li>Retrieve or check whether an element exists in constant time.</li>
                                <li>Keep performance predictable as the set grows.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Core Idea</h2>
                            <p className="system-design-description">
                                Use an array of buckets. A hash function converts each input into a hash value, and that value is mapped to a bucket index. The element is stored in the bucket at that index, so insertion and lookup usually touch only a small part of the collection.
                            </p>
                            <p className="system-design-description">
                                Two different inputs can produce the same bucket index. This is a hash collision. Each bucket therefore stores a list of elements, and a lookup checks the elements in that list until it finds a match or reaches the end.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Managing Collisions</h2>
                            <p className="system-design-description">
                                Searching a bucket list is linear in the number of elements inside it, so the design keeps collisions rare with a load factor. The load factor is the number of stored elements divided by the number of buckets:
                            </p>
                            <p className="system-design-description">
                                <strong>Load factor = number of elements / number of buckets</strong>
                            </p>
                            <p className="system-design-description">
                                A common threshold is 0.75. When the load factor reaches that threshold, the set creates twice as many buckets and rehashes every existing element into the new array. Rehashing costs linear time for that operation, but it happens infrequently, so insertion remains expected O(1) over time.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Why Keep the Load Factor Low?</h2>
                            <p className="system-design-description">
                                A load factor of 0.75 does not mean that three out of every four buckets contain exactly one element. Hashes are distributed unevenly, and collisions can leave some buckets empty while others hold multiple elements. This is related to the birthday paradox: collisions become likely much sooner than people intuitively expect.
                            </p>
                            <p className="system-design-description">
                                The trade-off is deliberate. The set uses additional memory for empty buckets in exchange for shorter collision chains and faster CPU operations. In practice, it sacrifices storage space to preserve expected constant-time performance.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Main Components</h2>
                            <ul>
                                <li><strong>Hash function:</strong> Converts an input into a hash value.</li>
                                <li><strong>Bucket array:</strong> Maps the hash value to a storage location.</li>
                                <li><strong>Collision chain:</strong> Holds elements that share the same bucket.</li>
                                <li><strong>Load factor threshold:</strong> Triggers a resize and rehash when buckets become too crowded.</li>
                            </ul>
                        </div>


                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>Working Logic</h2>
                            </div>
                            <pre className="design-code-block"><code>{hashSetDesignCode}</code></pre>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        Now if you'll excuse me, I have to go eat some food and get back to work. Go implement hashmaps on your own now, dummy!
                    </div>

                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default HashSetDesign;